import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

const LIVE_REGION_CLEAR_MS = 5000;
const QUANTITY_ERROR_DISPLAY_MS = 10000;

/** Cart add URL if global Theme is unavailable (mirrors layout `routes.cart_add_url` + `.js`) */
const FALLBACK_CART_ADD_URL = '/cart/add.js';

/**
 * Global `Theme` is defined in snippets/scripts.liquid; it is a global lexical binding, not `window.Theme`.
 * Same pattern as assets/product-form.js and assets/utilities.js (`typeof Theme`).
 * @returns {{ routes?: { cart_add_url?: string }; translations?: { added?: string } } | undefined}
 */
function getThemeGlobal() {
  if (typeof Theme === 'undefined') return undefined;
  return /** @type {any} */ (Theme);
}

/**
 * @typedef {{ variantId: number, value: string, type: string }} TShirtAddon
 */

class TShirtProperties extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLElement | null} */
  #productFormComponent = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    /**
     * Submit must be intercepted on window capture before Horizon's delegated listener on
     * `document` capture (see assets/component.js registerEventListeners). Otherwise
     * ProductFormComponent.handleSubmit runs first and duplicates `/cart/add`.
     */
    window.addEventListener('submit', this.#onFormSubmitCapture, { capture: true, signal });

    this.querySelectorAll('.t-shirt-properties__checkbox').forEach((checkbox) => {
      checkbox.addEventListener('change', this.#handleCheckboxChange.bind(this), { signal });
    });

    this.querySelectorAll('.t-shirt-properties__input').forEach((input) => {
      input.addEventListener('input', this.#handleTextInput.bind(this), { signal });
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  #onFormSubmitCapture = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    const form = event.target;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    const pfcEl = form.closest('product-form-component');
    if (!(pfcEl instanceof HTMLElement)) return;
    const myProductId = this.dataset.productId;
    if (!myProductId || pfcEl.dataset.productId !== myProductId) return;

    const addons = this.#getCheckedAddons();
    if (addons.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.#productFormComponent = pfcEl;
    this.#form = form;

    void this.#submitBundled(event);
  };

  /**
   * @param {SubmitEvent} submitEvent
   */
  async #submitBundled(submitEvent) {
    const form = this.#form;
    const pfc = this.#productFormComponent;

    if (!form || !pfc) {
      console.error('[t-shirt-properties] Missing form or product-form-component');
      return;
    }

    const pfRefs = /** @type {Record<string, any>} */ ((/** @type {any} */ (pfc)).refs ?? {});

    const allAddToCartContainers = /** @type {HTMLElement[]} */ ([...pfc.querySelectorAll('add-to-cart-component')]);
    const anyButtonDisabled = allAddToCartContainers.some((c) => /** @type {any} */ (c).refs?.addToCartButton?.disabled);
    if (anyButtonDisabled) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (pfRefs.quantitySelector?.canAddToCart) {
      const validation = pfRefs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        this.#showQuantityMaxError(pfc, pfRefs, validation.maxQuantity);
        return;
      }
    }

    const formData = new FormData(form);
    const mainVariantId = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity')) || Number(pfc.dataset.quantityDefault) || 1;

    // Collect any line item properties already on the form (e.g. from product-custom-property blocks)
    /** @type {Record<string, string>} */
    const mainProperties = {};
    for (const [key, val] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof val === 'string' && match[1] !== undefined) {
        mainProperties[match[1]] = val;
      }
    }

    const addons = this.#getCheckedAddons();

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, string>; parent_id?: number }>} */
    const items = [
      {
        id: mainVariantId,
        quantity,
        ...(Object.keys(mainProperties).length ? { properties: mainProperties } : {}),
      },
      ...addons.map((addon) => ({
        id: addon.variantId,
        quantity: 1,
        /** @type {Record<string, string>} */
        properties: addon.type === 'name' ? /** @type {Record<string, string>} */ ({ Navn: addon.value }) : /** @type {Record<string, string>} */ ({ Tal: addon.value }),
        parent_id: mainVariantId,
      })),
    ];

    const sectionIds = /** @type {string[]} */ ([]);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
        }),
        signal: this.#abortController?.signal,
      });

      const parsed = await response.json();

      if (parsed.status) {
        pfc.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', parsed.message, parsed.description, parsed.errors)
        );
        pfc.dispatchEvent(
          new CartAddEvent({}, pfc.id || '', {
            didError: true,
            source: 'product-form-component',
            itemCount: quantity,
            productId: pfc.dataset.productId,
          })
        );
        return;
      }

      const addToCartTextError = pfRefs.addToCartTextError;
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      const themeGlobal = getThemeGlobal();
      const anyAddToCartButton = /** @type {any} */ (allAddToCartContainers[0])?.refs?.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || themeGlobal?.translations?.added || '';

        const liveRegion = pfRefs.liveRegion;
        if (liveRegion && addedText) {
          liveRegion.textContent = addedText;
          setTimeout(() => {
            liveRegion.textContent = '';
          }, LIVE_REGION_CLEAR_MS);
        }
      }

      await this.#syncQuantitySelectorFromCart(pfc);

      pfc.dispatchEvent(
        new CartAddEvent({}, String(mainVariantId), {
          source: 'product-form-component',
          itemCount: quantity,
          productId: pfc.dataset.productId,
        })
      );
    } catch (err) {
      console.error('[t-shirt-properties]', err);
    } finally {
      cartPerformance.measureFromEvent('add:user-action', submitEvent);
    }
  }

  /**
   * @param {HTMLElement} pfc
   * @param {Record<string, any>} pfRefs
   * @param {number | undefined} maxQuantity
   */
  #showQuantityMaxError(pfc, pfRefs, maxQuantity) {
    const addToCartTextError = pfRefs.addToCartTextError;
    const errorTemplate = pfc.dataset.quantityErrorMax || '';
    const errorMessage = errorTemplate.replace('{{ maximum }}', maxQuantity?.toString() || '');

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');

      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = errorMessage;
      } else {
        addToCartTextError.appendChild(document.createTextNode(errorMessage));
      }

      const liveRegion = pfRefs.liveRegion;
      if (liveRegion) {
        liveRegion.textContent = errorMessage;
        setTimeout(() => {
          liveRegion.textContent = '';
        }, QUANTITY_ERROR_DISPLAY_MS);
      }

      setTimeout(() => {
        addToCartTextError.classList.add('hidden');
      }, QUANTITY_ERROR_DISPLAY_MS);
    }
  }

  /**
   * @param {HTMLElement} pfc
   */
  async #syncQuantitySelectorFromCart(pfc) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (pfc.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return;

    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      if (!cart?.items) return;

      const cartItem = cart.items.find(
        (/** @type {{ variant_id: number }} */ item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      const quantitySelector = pfc.querySelector('quantity-selector-component');
      if (quantitySelector && 'setCartQuantity' in quantitySelector && typeof quantitySelector.setCartQuantity === 'function') {
        quantitySelector.setCartQuantity(cartQty);
      }

      const quantityLabelCartCount = /** @type {HTMLElement | undefined} */ (
        /** @type {any} */ (pfc).refs?.quantityLabelCartCount
      );
      if (quantityLabelCartCount) {
        const inCartText = quantityLabelCartCount.textContent?.match(/\((\d+)\s+(.+)\)/);
        if (inCartText && inCartText[2]) {
          quantityLabelCartCount.textContent = `(${cartQty} ${inCartText[2]})`;
        }
        quantityLabelCartCount.classList.toggle('hidden', cartQty === 0);
      }
    } catch (e) {
      console.error('[t-shirt-properties] Failed to sync quantity selector', e);
    }
  }

  /**
   * @param {Event} event
   */
  #handleCheckboxChange(event) {
    const checkbox = /** @type {HTMLInputElement | null} */ (event.target);
    const option = checkbox?.closest('.t-shirt-properties__option');
    if (!option || !(checkbox instanceof HTMLInputElement)) return;

    const fieldWrapper = option.querySelector('.t-shirt-properties__field-wrapper');
    const textInput = /** @type {HTMLInputElement | null} */ (option.querySelector('.t-shirt-properties__input'));

    if (checkbox.checked) {
      option.classList.add('t-shirt-properties__option--checked');
      fieldWrapper?.removeAttribute('aria-hidden');
      if (textInput) {
        textInput.removeAttribute('tabindex');
        textInput.focus();
      }
    } else {
      option.classList.remove('t-shirt-properties__option--checked');
      fieldWrapper?.setAttribute('aria-hidden', 'true');
      if (textInput) {
        textInput.setAttribute('tabindex', '-1');
        textInput.value = '';
        this.#updateHelper(textInput);
      }
    }
  }

  /**
   * @param {Event} event
   */
  #handleTextInput(event) {
    const input = /** @type {HTMLInputElement | null} */ (event.target);
    if (!(input instanceof HTMLInputElement)) return;

    const addonCheckbox = /** @type {HTMLElement | null} */ (
      input.closest('.t-shirt-properties__option')?.querySelector('.t-shirt-properties__checkbox')
    );
    const addonType = addonCheckbox?.dataset.addon;

    if (addonType === 'number') {
      let val = input.value.replace(/\D/g, '');
      if (val.length > 3) val = val.slice(0, 3);
      if (val !== input.value) input.value = val;
    }

    this.#updateHelper(input);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #updateHelper(input) {
    const wrapper = input.closest('.t-shirt-properties__field-inner');
    const helper = wrapper?.querySelector('.t-shirt-properties__helper-text');
    if (helper) {
      helper.textContent = `${input.value.length} / ${input.maxLength}`;
    }
  }

  /**
   * @returns {TShirtAddon[]}
   */
  #getCheckedAddons() {
    /** @type {TShirtAddon[]} */
    const addons = [];

    this.querySelectorAll('.t-shirt-properties__checkbox:checked').forEach((checkboxEl) => {
      const checkbox = /** @type {HTMLInputElement} */ (checkboxEl);
      const option = checkbox.closest('.t-shirt-properties__option');
      const input = /** @type {HTMLInputElement | null} */ (option?.querySelector('.t-shirt-properties__input'));
      const addonType = checkbox.dataset.addon;
      const variantIdAttr = addonType === 'name' ? 'data-name-variant-id' : 'data-number-variant-id';
      const variantId = this.getAttribute(variantIdAttr);

      if (variantId && input?.value?.trim()) {
        addons.push({
          variantId: parseInt(variantId, 10),
          value: input.value.trim(),
          type: addonType ?? '',
        });
      }
    });

    return addons;
  }
}

if (!customElements.get('t-shirt-properties')) {
  customElements.define('t-shirt-properties', TShirtProperties);
}
